/**
 * 피벗 표를 **다시 묻지 않고** 바꿔 보는 두 가지 — 잣대 갈아 끼우기 · 줄 접기.
 *
 * 🔑 `analytics.pivot` 은 칸마다 `{ value, count }` 를 함께 준다 — `value` 는 고른 지표,
 *   `count` 는 늘 **글 수**다. 그래서 「도달」로 한 번만 물어도 「언급」은 이미 손에 있다.
 *   ⚠️ 그림 부품의 `read()` 는 **`metric` 을 보지 않고 `value` 만 읽는다** — 잣대를 바꾸려면
 *   지표 이름만 바꿔서는 안 되고 **값 자체를 갈아 끼워야** 한다(`byCount`).
 */

import type { PivotCell, PivotTable } from 'src/utils/pivot/pivot';

import { ALL_LABEL } from 'src/utils/pivot/pivot';

/** 보이는 숫자를 **글 수**로 — 도달로 물어 온 표를 언급 표로 읽는다 */
export function byCount(table: PivotTable | null): PivotTable | null {
  if (!table) return null;
  const swap = (cell: PivotCell | undefined): PivotCell => ({ value: cell?.count ?? 0, count: cell?.count ?? 0 });
  return {
    ...table,
    cells: Object.fromEntries(
      Object.entries(table.cells).map(([row, columns]) => [
        row,
        Object.fromEntries(Object.entries(columns).map(([column, cell]) => [column, swap(cell)])),
      ]),
    ),
    rowTotals: Object.fromEntries(Object.entries(table.rowTotals).map(([row, cell]) => [row, swap(cell)])),
    columnTotals: Object.fromEntries(Object.entries(table.columnTotals).map(([column, cell]) => [column, swap(cell)])),
    total: swap(table.total),
  };
}

/**
 * 줄 **이름만** 바꾼다 — 집계가 준 원값(`tiktok`·`naverblog`)을 화면 말(`TikTok`·`N.Blog`)로.
 *
 * 🚨 그림 부품은 축이 무엇인지 모른다 — **표를 넘기는 쪽이 이름을 지어 줘야** 범례가 원값으로 새지 않는다.
 * ⚠️ 이름이 겹치면 **뒤엣것이 앞엣것을 덮는다** — 채널·제품군처럼 1:1로 옮겨지는 축에만 쓴다.
 * @param name 원값 → 화면 말
 */
export function renameRows(table: PivotTable | null, name: (row: string) => string): PivotTable | null {
  if (!table) return null;
  const rows = table.rows.map(name);
  return {
    ...table,
    rows,
    cells: Object.fromEntries(table.rows.map((row) => [name(row), table.cells[row] ?? {}])),
    rowTotals: Object.fromEntries(table.rows.map((row) => [name(row), table.rowTotals[row] ?? { value: 0, count: 0 }])),
  };
}

/**
 * 줄을 **한 줄로 접는다** — 채널별로 갈라 본 표를 「전체」 한 줄로.
 *
 * ⚠️ 열(시간)은 그대로 둔다 — 접는 것은 줄뿐이다.
 * @param label 접고 난 한 줄의 이름
 */
export function foldRows(table: PivotTable | null, label: string = ALL_LABEL): PivotTable | null {
  if (!table) return null;
  const sum = (column: string): PivotCell =>
    table.rows.reduce<PivotCell>(
      (acc, row) => {
        const cell = table.cells[row]?.[column];
        return { value: acc.value + (cell?.value ?? 0), count: acc.count + (cell?.count ?? 0) };
      },
      { value: 0, count: 0 },
    );

  const cells = Object.fromEntries(table.columns.map((column) => [column, sum(column)]));
  return {
    rows: [label],
    columns: table.columns,
    cells: { [label]: cells },
    rowTotals: { [label]: table.total },
    columnTotals: table.columnTotals,
    total: table.total,
  };
}
